import Editor from "@monaco-editor/react";
import ApiIcon from "@mui/icons-material/Api";
import CachedIcon from "@mui/icons-material/Cached";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  capitalize,
  Dialog,
  DialogContent,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type React from "react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { STATUS_COLORS } from "@/theme/theme";
import { useApiQuery } from "../hooks/use-api-query";
import type { CheckRun, TestRunStep } from "../types";
import { formatDuration } from "../utils";
import { clickableProps } from "../utils/keyboard";
import type { StepTreeMetadata } from "../utils/step-tree-utils";
import { humanizeText } from "../utils/text";
import { RunStatusChip } from "./run-status-chip";

interface TestRunStepCardProps {
  step: TestRunStep;
  onClick?: () => void;
  isClickable?: boolean;
  // Props for tree rendering
  treeMetadata?: StepTreeMetadata;
  isCollapsed?: boolean;
  onToggleCollapse?: (pathKey: string) => void;
  childCount?: number;
}

export const TestRunStepCard: React.FC<TestRunStepCardProps> = ({
  step,
  onClick,
  isClickable = false,
  treeMetadata,
  isCollapsed = false,
  onToggleCollapse,
  childCount = 0,
}) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const depth = treeMetadata?.depth ?? 0;
  const isParent = treeMetadata?.isParent ?? false;
  const pathKey = treeMetadata?.pathKey ?? step.name;

  const isCheckRun = step.type === "check_run";

  // For embedded API-check steps, resolve the parent check_id from the linked
  // CheckRun so "View check run" can deep-link into the check detail page.
  const { data: checkRun } = useApiQuery<CheckRun>(`/check-runs/${step.entity_id}`, {
    enabled: isCheckRun && !!step.entity_id,
    refetchInterval: false,
  });

  const handleViewCheckRun = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (checkRun?.check_id && step.entity_id) {
      navigate(`/checks/${checkRun.check_id}?tab=runs&runId=${step.entity_id}`);
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger onClick if clicking on accordion, expand button, or collapse toggle
    const target = e.target as HTMLElement;
    if (
      target.closest(".MuiAccordion-root") ||
      target.closest(".expand-button") ||
      target.closest(".collapse-toggle")
    ) {
      return;
    }
    onClick?.();
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  const handleCollapseToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleCollapse?.(pathKey);
  };

  const canExpand = useMemo(() => {
    if (!step?.type) return false;
    return ["block_run", "pw_run"].includes(step.type);
  }, [step.type]);

  // Indentation based on depth
  const indentPx = depth * 24;

  const cardContent = (
    <Box
      sx={{
        paddingLeft: `${indentPx}px`,
        position: "relative",
      }}
    >
      {/* Vertical line for nested steps */}
      {depth > 0 && (
        <Box
          sx={{
            position: "absolute",
            left: indentPx - 12,
            top: 0,
            bottom: 0,
            width: "2px",
            backgroundColor: "divider",
            opacity: 0.5,
          }}
        />
      )}
      <Card
        sx={{
          cursor: isClickable ? "pointer" : "default",
          border: "1px solid",
          backgroundColor:
            step.type === "cached_dependency"
              ? STATUS_COLORS.purple.bg // Light purple for cached
              : step.retry_phase === "initial"
                ? STATUS_COLORS.warning.bg // Light amber for initial retries
                : step.retry_phase === "fixing"
                  ? STATUS_COLORS.purple.bg // Light purple for fixing
                  : step.run_status === "running"
                    ? STATUS_COLORS.info.bg
                    : depth > 0
                      ? `rgba(0, 0, 0, ${0.01 * depth})`
                      : "white",
          borderColor:
            step.type === "cached_dependency"
              ? STATUS_COLORS.purple.border // Purple border for cached
              : step.run_status === "failed"
                ? "error.main"
                : step.run_status === "running"
                  ? "primary.main"
                  : "divider",
        }}
        {...clickableProps(handleCardClick)}
        onClick={handleCardClick}
      >
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Stack direction="column" spacing={1.5}>
            {/* Header with collapse toggle, timestamp, type, and expand button */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1}>
                {/* Collapse toggle for parent steps */}
                {isParent && (
                  <IconButton
                    aria-label={isCollapsed ? "Expand" : "Collapse"}
                    className="collapse-toggle"
                    size="small"
                    onClick={handleCollapseToggle}
                    sx={{ p: 0.25 }}
                  >
                    {isCollapsed ? (
                      <ChevronRightIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                )}
                <Typography variant="caption" color="text.secondary">
                  {new Date(step.created_at).toLocaleString()}
                </Typography>
                {isParent && childCount > 0 && (
                  <Chip
                    size="small"
                    label={`${childCount} step${childCount > 1 ? "s" : ""}`}
                    sx={{ height: 18, fontSize: "0.7rem" }}
                  />
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                {step.type && (
                  <Chip
                    variant="outlined"
                    size="small"
                    icon={
                      step.type === "cached_dependency" ? (
                        <CachedIcon fontSize="small" />
                      ) : isCheckRun ? (
                        <ApiIcon fontSize="small" />
                      ) : undefined
                    }
                    sx={
                      step.type === "cached_dependency"
                        ? {
                            borderColor: STATUS_COLORS.purple.border,
                            color: STATUS_COLORS.purple.text,
                            "& .MuiChip-icon": { color: STATUS_COLORS.purple.text },
                          }
                        : undefined
                    }
                    color={
                      step.type === "cached_dependency"
                        ? "default"
                        : isCheckRun
                          ? "info"
                          : ["block_run", "pw_run"].includes(step.type)
                            ? "primary"
                            : "default"
                    }
                    label={
                      step.type === "cached_dependency"
                        ? "Cached"
                        : isCheckRun
                          ? "API Check"
                          : humanizeText(step.type)
                    }
                  />
                )}
                {canExpand && (
                  <Tooltip title={expanded ? "Collapse" : "Expand details"}>
                    <IconButton
                      aria-label={expanded ? "Collapse" : "Expand details"}
                      className="expand-button"
                      size="small"
                      onClick={handleExpandClick}
                    >
                      {expanded ? (
                        <CloseFullscreenIcon fontSize="small" />
                      ) : (
                        <OpenInFullIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Stack>

            {/* Step details */}
            <Stack direction="column" spacing={0}>
              <Stack direction="row" alignItems="center" spacing={1}>
                {step.type === "cached_dependency" && (
                  <CachedIcon
                    fontSize="small"
                    sx={{ color: STATUS_COLORS.purple.text, flexShrink: 0 }}
                  />
                )}
                <Typography variant="subtitle1">{step.name}</Typography>
              </Stack>
              {step.description && (
                <Typography variant="body2" color="text.secondary">
                  {step.description}
                </Typography>
              )}
              {isCheckRun && checkRun?.check_id && (
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  underline="hover"
                  onClick={handleViewCheckRun}
                  sx={{
                    mt: 0.5,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    alignSelf: "flex-start",
                  }}
                >
                  View check run
                  <OpenInNewIcon sx={{ fontSize: "0.9rem" }} />
                </Link>
              )}
            </Stack>

            {/* Code display */}
            {step.code && (
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "4px",
                }}
              >
                <Editor
                  height="80px"
                  width="100%"
                  defaultLanguage="python"
                  value={step.code}
                  options={{
                    readOnly: true,
                    minimap: { enabled: false },
                    lineNumbers: "off",
                    scrollBeyondLastLine: false,
                  }}
                />
              </Box>
            )}

            {/* Logs Accordion */}
            {step.logs && step.logs.length > 0 && (
              <Accordion disableGutters elevation={0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2">Logs</Typography>
                    <Chip
                      label={step.logs.length}
                      size="small"
                      sx={{ height: 18, fontSize: "0.7rem" }}
                    />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "4px",
                      p: 1,
                      backgroundColor: "grey.100",
                      fontFamily: "monospace",
                      fontSize: "0.85em",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      maxHeight: "300px",
                      overflow: "auto",
                    }}
                  >
                    {step.logs.join("\n")}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Error Accordion */}
            {step.error_message && (
              <Accordion disableGutters elevation={0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" color="error">
                    Error Message
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "error.main",
                      borderRadius: "4px",
                      p: 1,
                      backgroundColor: STATUS_COLORS.error.bg,
                      fontFamily: "monospace",
                      fontSize: "0.85em",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      maxHeight: "300px",
                      overflow: "auto",
                    }}
                  >
                    {step.error_message}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Footer with status and timing */}
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <RunStatusChip
                status={step.run_status}
                label={
                  step.retry_phase
                    ? step.retry_phase === "fixing"
                      ? `Fixing (${step.retry_attempt}/${step.max_retries})`
                      : `Retrying (${step.retry_attempt}/${step.max_retries})`
                    : undefined
                }
                retryPhase={step.retry_phase}
              />
              {typeof step.elapsed_seconds === "number" && (
                <Typography variant="caption" color="text.secondary">
                  {formatDuration(step.elapsed_seconds, { decimals: 3 })}
                </Typography>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );

  // Expanded dialog view
  if (expanded) {
    return (
      <Dialog open={true} onClose={() => setExpanded(false)} maxWidth="lg" fullWidth>
        <DialogContent sx={{ p: 0 }}>
          <Card sx={{ border: "none" }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="column" spacing={3}>
                {/* Header */}
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="column" spacing={0.5}>
                    <Typography variant="h6">{step.name}</Typography>
                    {step.description && (
                      <Typography variant="body2" color="text.secondary">
                        {step.description}
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {step.type && (
                      <Chip
                        variant="outlined"
                        color={["block_run", "pw_run"].includes(step.type) ? "primary" : "default"}
                        label={capitalize(step.type.replace(/_/g, " "))}
                      />
                    )}
                    <RunStatusChip
                      status={step.run_status}
                      label={
                        step.retry_phase
                          ? step.retry_phase === "fixing"
                            ? `Fixing (${step.retry_attempt}/${step.max_retries})`
                            : `Retrying (${step.retry_attempt}/${step.max_retries})`
                          : undefined
                      }
                      retryPhase={step.retry_phase}
                    />
                    <IconButton
                      aria-label="Collapse"
                      size="small"
                      onClick={() => setExpanded(false)}
                    >
                      <CloseFullscreenIcon />
                    </IconButton>
                  </Stack>
                </Stack>

                {/* Metadata */}
                <Stack direction="row" spacing={3}>
                  <Typography variant="caption" color="text.secondary">
                    Created: {new Date(step.created_at).toLocaleString()}
                  </Typography>
                  {typeof step.elapsed_seconds === "number" && (
                    <Typography variant="caption" color="text.secondary">
                      Duration: {formatDuration(step.elapsed_seconds, { decimals: 3 })}
                    </Typography>
                  )}
                </Stack>

                {/* Code */}
                {step.code && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Code
                    </Typography>
                    <Box
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "4px",
                      }}
                    >
                      <Editor
                        height="300px"
                        width="100%"
                        defaultLanguage="python"
                        value={step.code}
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                          lineNumbers: "on",
                          scrollBeyondLastLine: false,
                        }}
                      />
                    </Box>
                  </Box>
                )}

                {/* Screenshots side by side - placeholder for now */}
                {/* TODO: Add screenshot support when available in data */}

                {/* Logs */}
                {step.logs && step.logs.length > 0 && (
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <Typography variant="subtitle2">Logs</Typography>
                      <Chip
                        label={step.logs.length}
                        size="small"
                        sx={{ height: 18, fontSize: "0.7rem" }}
                      />
                    </Stack>
                    <Box
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "4px",
                        p: 2,
                        backgroundColor: "grey.100",
                        fontFamily: "monospace",
                        fontSize: "0.9em",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        maxHeight: "400px",
                        overflow: "auto",
                      }}
                    >
                      {step.logs.join("\n")}
                    </Box>
                  </Box>
                )}

                {/* Error */}
                {step.error_message && (
                  <Box>
                    <Typography variant="subtitle2" color="error" gutterBottom>
                      Error Message
                    </Typography>
                    <Box
                      sx={{
                        border: "1px solid",
                        borderColor: "error.main",
                        borderRadius: "4px",
                        p: 2,
                        backgroundColor: STATUS_COLORS.error.bg,
                        fontFamily: "monospace",
                        fontSize: "0.9em",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        maxHeight: "400px",
                        overflow: "auto",
                      }}
                    >
                      {step.error_message}
                    </Box>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>
    );
  }

  return cardContent;
};
